import { useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useNow } from '../hooks/useNow';
import { usePlanetResource } from '../hooks/usePlanetResource';
import QueueItemCard from '../components/QueueItemCard';
import UnlockSlotsButton from '../components/UnlockSlotsButton';
import { hasHigherQueueTier } from '../utils/queueUpsell';
import ScanForSpiesButton from '../components/ScanForSpiesButton';
import ScienceCard, { SCIENCE_CATEGORY_ORDER, SCIENCE_CATEGORY_TITLES, type ScienceCardProps } from '../components/ScienceCard';
import { formatCountdown } from '../utils/formatters';
import { getScienceIcon } from '../utils/scienceIcons';
import { eventProgress } from '../utils/eventProgress';
import { Skeleton } from '../components/ui/Skeleton';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import {
    queueScience,
    cancelScienceResearch,
    rushScienceResearch,
    type ResearchActionOptions,
} from '../services/science.service';
import {
    SCIENCE_DEFINITIONS,
    RESEARCH_QUEUE_LIMITS,
    getResearchQueueLimit,
    type PlayerRace,
    type ScienceId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateRushCost,
    extractBuildingLevels,
    isScienceRushable,
} from '@kaeliux/game-engine';

interface QueuedResearchRowProps {
    planetId: string;
    event: TickCheckpointEventOf<'scienceResearch'>;
    now: number;
    options: ResearchActionOptions;
}

/** One queued/researching entry within a science's own card — mirrors ZonePage's QueuedUpgradeRow. */
function QueuedResearchRow({ planetId, event, now, options }: QueuedResearchRowProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRushing, setIsRushing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);

    const def = SCIENCE_DEFINITIONS[event.eventDetails.scienceId];
    const startMs = event.eventDetails.startDate.getTime();
    const endMs = event.endDate.getTime();
    const started = startMs <= now;
    const remainingMs = endMs - now;
    const progress = eventProgress(startMs, endMs, now);
    const rushable = isScienceRushable(event.eventDetails.scienceId);
    // A not-yet-started item only has its OWN duration collapsed to zero by a
    // rush — it still waits for whatever's ahead of it in the queue — so
    // pricing it off the full remaining-to-completion time (which includes
    // that wait) would charge for wall-clock time the rush can't actually
    // remove (round 3 review, finding #2).
    const rushCost = calculateRushCost(started ? remainingMs / 1_000 : event.eventDetails.durationSeconds);
    const alreadyRushed = event.eventDetails.durationSeconds === 0;

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            const result = await cancelScienceResearch(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: result.planet });
            usePlayerStore.setState({ sciences: result.player.sciences, tickCheckpoints: result.player.tickCheckpoints });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    async function doRush() {
        setIsRushing(true);
        setError(null);
        try {
            const result = await rushScienceResearch(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: result.planet });
            usePlayerStore.setState({
                sciences: result.player.sciences,
                tickCheckpoints: result.player.tickCheckpoints,
                ...(result.premium ? { premium: result.premium } : {}),
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Rush failed');
        } finally {
            setIsRushing(false);
        }
    }

    function requestCancel() {
        openDialog({
            title: `Cancel ${def.name} research?`,
            message: 'You will be refunded 60% of the resources spent.',
            confirmLabel: 'Cancel research',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    function requestRush() {
        openDialog({
            title: `Rush ${def.name} research?`,
            message: started
                ? `Instantly complete this for ${rushCost} Dark Matter. This is non-refundable.`
                : `Remove this item's own research time for ${rushCost} Dark Matter. It will still wait for the item ahead of it in the queue. This is non-refundable.`,
            confirmLabel: 'Rush',
            onConfirm: () => { doRush().catch(() => { }); },
        });
    }

    return (
        <QueueItemCard
            image={
                <img
                    src={getScienceIcon(event.eventDetails.scienceId)}
                    alt=""
                    className="w-full h-full object-cover rounded-tile bg-surface-void"
                />
            }
            overlayLabel={`Level ${event.eventDetails.targetLevel}`}
            name={def.name}
            statusText={`${started ? 'Researching…' : 'Queued —'} ${formatCountdown(remainingMs)}`}
            progress={progress}
            error={error}
            isCancelling={isCancelling}
            {...(rushable ? {
                rushCost,
                isRushing,
                rushDisabled: darkMatter < rushCost || alreadyRushed,
                rushDisabledReason: alreadyRushed ? 'Rushed' : undefined,
                onRush: requestRush,
            } : {})}
            onCancel={requestCancel}
            imageBoxClassName="w-14 aspect-square"
        />
    );
}

interface ScienceRowProps {
    scienceId: ScienceId;
    planetId: string;
    race: PlayerRace | undefined;
    resources: ScienceCardProps['resources'];
    laboratoryLevel: number;
    speedMultiplier: number;
    scienceLevels: Partial<Record<ScienceId, number>>;
    buildingLevels: ScienceCardProps['buildingLevels'];
    queuedEvents: TickCheckpointEventOf<'scienceResearch'>[];
    queueFull: boolean;
    now: number;
    options: ResearchActionOptions;
    highlighted?: boolean;
    /** Intelligence row only — feeds the "Scan for Spy Ships" cooldown. */
    lastSpyScanAt?: Date;
}

function ScienceRow({ scienceId, planetId, queuedEvents, now, options, lastSpyScanAt, ...card }: ScienceRowProps) {
    async function research() {
        const result = await queueScience(planetId, scienceId, options);
        usePlanetStore.setState({ currentPlanet: result.planet });
        usePlayerStore.setState({ sciences: result.player.sciences, tickCheckpoints: result.player.tickCheckpoints });
    }

    return (
        <ScienceCard
            {...card}
            scienceId={scienceId}
            queuedCount={queuedEvents.length}
            onResearch={research}
            queueItems={queuedEvents.length > 0 ? queuedEvents.map((event) => (
                <QueuedResearchRow key={event.eventId} planetId={planetId} event={event} now={now} options={options} />
            )) : undefined}
            footer={scienceId === 'intelligence' ? (
                <ScanForSpiesButton
                    planetId={planetId}
                    intelligenceLevel={card.scienceLevels.intelligence ?? 0}
                    lastSpyScanAt={lastSpyScanAt}
                    className="border-t border-hairline pt-3"
                />
            ) : undefined}
        />
    );
}

function SciencesPageContent({ planetId }: { planetId: string }) {
    const { state: { planet, isLoading: isPlanetLoading } } = usePlanetResource();
    const { profile } = useAuthStore();
    const { sciences, tickCheckpoints, error: loadError } = usePlayerStore();
    const effectiveTier = usePlayerStore(selectEffectiveTier);
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();
    const [searchParams] = useSearchParams();
    const highlightScienceId = searchParams.get('highlight');

    const options: ResearchActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier]
    );

    const scienceLevels = useMemo(
        () => (sciences ?? {}) as Partial<Record<ScienceId, number>>,
        [sciences]
    );

    const queue = useMemo(
        () => tickCheckpoints.filter(
            (e): e is TickCheckpointEventOf<'scienceResearch'> => e.eventType === 'scienceResearch' && !e.cancelled
        ),
        [tickCheckpoints]
    );

    const buildingLevels = useMemo(
        () => (planet ? extractBuildingLevels(planet.buildings) : ({} as ReturnType<typeof extractBuildingLevels>)),
        [planet]
    );

    // Effective tier (base tier + any unexpired boost) from usePlayerStore's
    // premium state — see selectEffectiveTier in playerStore.ts.
    const queueLimit = getResearchQueueLimit(effectiveTier);
    const queueFull = queue.length >= queueLimit;
    const laboratoryLevel = planet?.buildings.laboratory?.level ?? 0;

    const resources = useMemo(() => {
        if (!planet) return { metal: 0, minerals: 0, gas: 0, energy: 0, ozone: 0 };
        return {
            metal: planet.resources.metal.current,
            minerals: planet.resources.minerals.current,
            gas: planet.resources.gas.current,
            energy: planet.resources.energy.rate - (planet.resources.energy.consumed ?? 0),
            ozone: planet.state.ozone.current,
        };
    }, [planet]);

    if (isPlanetLoading || !planet || sciences === null) {
        return (
            <div className="min-h-screen bg-space-900 flex flex-col items-center justify-center gap-3 px-4 w-full max-w-md mx-auto" style={zoneAccentStyle('military')}>
                <span className="text-title text-content-muted">{loadError ?? 'Loading research…'}</span>
                <Skeleton variant="block" className="w-full h-24" />
                <Skeleton variant="block" className="w-full h-24" />
            </div>
        );
    }

    if (laboratoryLevel === 0) {
        return <Navigate to="/zone/military?highlight=laboratory" replace />;
    }

    return (
        <div className="bg-transparent flex flex-col" style={zoneAccentStyle('military')}>
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-display text-content-primary">Sciences</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-body text-content-muted">
                            Queue: {queue.length} / {queueLimit}
                        </span>
                        {queueFull && hasHigherQueueTier(RESEARCH_QUEUE_LIMITS, effectiveTier) && <UnlockSlotsButton />}
                        <Link to="/planet" className={linkButtonClasses('secondary')}>← Back to Planet</Link>
                    </div>
                </div>

                {SCIENCE_CATEGORY_ORDER.map((category) => {
                    const categorySciences = (Object.values(SCIENCE_DEFINITIONS))
                        .filter((def) => def.category === category);
                    return (
                        <div key={category} className="flex flex-col gap-3">
                            <h2 className="text-heading text-content-secondary">{SCIENCE_CATEGORY_TITLES[category]}</h2>
                            <div className="flex flex-col gap-3">
                                {categorySciences.map((def) => (
                                    <ScienceRow
                                        key={def.id}
                                        scienceId={def.id}
                                        planetId={planetId}
                                        race={profile?.race}
                                        resources={resources}
                                        laboratoryLevel={laboratoryLevel}
                                        speedMultiplier={speedMultiplier}
                                        scienceLevels={scienceLevels}
                                        buildingLevels={buildingLevels}
                                        queuedEvents={queue.filter((e) => e.eventDetails.scienceId === def.id)}
                                        queueFull={queueFull}
                                        now={now}
                                        options={options}
                                        highlighted={def.id === highlightScienceId}
                                        lastSpyScanAt={planet?.lastSpyScanAt}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </main>
        </div>
    );
}

export default function SciencesPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <span className="text-title text-content-muted">Loading…</span>
            </div>
        );
    }

    return (
        <SciencesPageContent planetId={planetId} />
    );
}
